import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Building2, Lock, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { API_URL } from "../../lib/utils";

export const AcceptInvitePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Lien d'invitation invalide");
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 8) {
            toast.error("Le mot de passe doit contenir au moins 8 caractères");
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/users/accept-invite?token=${token}&password=${encodeURIComponent(password)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            
            const data = await res.json();
            
            if (!res.ok) {
                throw new Error(data.detail || "Erreur lors de l'acceptation");
            }
            
            // Save token and redirect
            localStorage.setItem("orkestria_token", data.access_token);
            setSuccess(true);
            toast.success("Compte créé avec succès !");
            
            setTimeout(() => {
                navigate("/dashboard");
                window.location.reload();
            }, 2000);
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
                        <h2 className="text-xl font-bold mb-2">Invitation invalide</h2>
                        <p className="text-muted-foreground mb-4">{error}</p>
                        <Button onClick={() => navigate("/login")}>Aller à la connexion</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                        <h2 className="text-xl font-bold mb-2">Bienvenue !</h2>
                        <p className="text-muted-foreground">Votre compte a été créé. Redirection...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-slide-up">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <img src="/orkestria-logo.png" alt="Orkestria" className="w-14 h-14 rounded-2xl object-contain mb-4" />
                    <h1 className="font-heading font-bold text-2xl text-foreground">Orkestria</h1>
                    <p className="text-sm text-muted-foreground">Gestion de Projets</p>
                </div>

                <Card>
                    <CardHeader className="text-center">
                        <CardTitle className="font-heading text-xl">Créer votre compte</CardTitle>
                        <CardDescription>
                            Vous avez été invité(e) à rejoindre une équipe. Définissez votre mot de passe pour activer votre compte.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Mot de passe</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Au moins 8 caractères"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10"
                                        required
                                        minLength={8}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="Répétez le mot de passe"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10"
                                        required
                                    />
                                </div>
                            </div>

                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Création en cours...
                                    </>
                                ) : (
                                    "Créer mon compte"
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
